/**
 * Política de renovação de sessão: só os números e a decisão, sem React. É a mesma do
 * busipet-front-admin.
 *
 * Fica separada do componente porque é a parte que se testa: um relógio, uma marca de atividade
 * e uma escolha entre três. O `VigiaDeSessao` só obedece.
 *
 * ## O problema que isto resolve
 *
 * O token vive 15 minutos (`identity-service.jwt.work-factor`) e ninguém avisava nada: o usuário
 * preenchia um cadastro, clicava em salvar e caía no login com o formulário perdido.
 *
 * ## Por que não renovar sempre, em silêncio
 *
 * Porque a sessão mora no navegador de uma máquina que pode estar destravada e sozinha. Renovar
 * sem condição manteria a sessão viva enquanto a aba existisse. Os 15 minutos virariam tempo
 * indefinido, e sumiria o único limite que protege quem esqueceu o backoffice aberto.
 *
 * ## Por que não só o aviso
 *
 * Porque interromper quem está trabalhando para perguntar se quer continuar trabalhando é uma
 * pergunta cuja resposta já está na tela. E seria a cada ~13,5 minutos.
 *
 * Daí a regra: **quem está usando não é interrompido; quem parou é consultado.** Atividade recente
 * renova sozinha. Ociosidade abre o aviso, e o silêncio diante dele encerra a sessão, que é o
 * comportamento certo para uma estação abandonada.
 */

/**
 * Quanto tempo sem interação até considerar o usuário ausente. Quem passa mais de 2 minutos só
 * lendo, sem clicar nem digitar, recebe o aviso perto do fim, e um clique continua a sessão.
 */
export const MS_OCIOSIDADE = 120_000;

/**
 * A partir de quanto tempo restante renovar sem perguntar, havendo atividade.
 *
 * 2min30 dá um minuto de folga sobre o aviso. Se a renovação falhar por rede instável, ainda sobra
 * tempo para tentar de novo e, não dando, para o aviso aparecer antes do fim.
 */
export const MS_RENOVACAO_SILENCIOSA = 150_000;

/** A partir de quanto tempo restante avisar o usuário ocioso. */
export const MS_AVISO = 90_000;

/** De quanto em quanto tempo a decisão é reavaliada. */
export const MS_INTERVALO_VERIFICACAO = 5_000;

/**
 * Espera mínima entre tentativas depois de uma falha.
 *
 * Sem isto, API fora do ar durante a janela de renovação vira uma requisição a cada 5 segundos
 * até a sessão morrer: trinta chamadas para um serviço que já está em apuros.
 */
export const MS_ESPERA_APOS_FALHA = 20_000;

/**
 * A decisão inteira, em um lugar só: `'nada'`, `'renovar'` ou `'avisar'`.
 *
 * A ordem das guardas importa:
 *
 * 1. **Sessão já vencida:** nada a fazer. Renovar com token vencido é 401 garantido, e quem trata
 *    a expiração é o `AuthProvider`.
 * 2. **Renovação em curso ou aviso aberto:** a decisão já foi tomada.
 * 3. **Ativo e dentro da janela:** renova, sem perguntar.
 * 4. **Ocioso e perto do fim:** avisa.
 *
 * A espera depois de uma falha bloqueia só a renovação automática, **nunca o aviso**. Se a rede
 * caiu, o usuário precisa saber que a sessão está acabando ainda mais do que quando tudo funciona.
 * O botão do aviso é dele, e não respeita a espera.
 *
 * @param {object} estado
 * @param {number} estado.msRestantes milissegundos até o token expirar
 * @param {number} estado.msDesdeAtividade milissegundos desde a última interação
 * @param {boolean} estado.renovando já existe uma renovação em curso
 * @param {boolean} estado.avisoAberto o aviso já está na tela
 * @param {number|null} estado.msDesdeFalha milissegundos desde a última falha; nulo se não houve
 * @returns {'nada'|'renovar'|'avisar'}
 */
export function decidir(estado) {
    if (estado.msRestantes <= 0) return 'nada';
    if (estado.renovando) return 'nada';
    if (estado.avisoAberto) return 'nada';

    const ativo = estado.msDesdeAtividade < MS_OCIOSIDADE;
    const emEspera = estado.msDesdeFalha !== null && estado.msDesdeFalha < MS_ESPERA_APOS_FALHA;

    if (ativo && estado.msRestantes <= MS_RENOVACAO_SILENCIOSA) {
        return emEspera ? 'nada' : 'renovar';
    }

    if (!ativo && estado.msRestantes <= MS_AVISO) return 'avisar';

    return 'nada';
}

/** Segundos restantes, arredondados para cima: o que o contador mostra. */
export function segundosRestantes(msRestantes) {
    return Math.max(0, Math.ceil(msRestantes / 1000));
}

/** `1:30`, `0:07`. Contador de tempo curto não se escreve por extenso. */
export function formatarContagem(msRestantes) {
    const total = segundosRestantes(msRestantes);
    const minutos = Math.floor(total / 60);
    const segundos = total % 60;
    return `${minutos}:${String(segundos).padStart(2, '0')}`;
}
